// Settings › Follow-up › "When ELVA flags it" (D228). Mock only: one small shared store, same
// pattern as the other mock stores, so every page that shows these numbers reads the same values.
import { useSyncExternalStore } from 'react';
import { recordSettingsChange } from '../settings-d/settingsEdit';

export interface FlagSettings {
  /** A claim that was made but not sent: flagged after this many days. */
  notSentDays: number;
  /** A claim that hasn't been paid: flagged this many days after it was first sent. */
  unpaidDays: number;
  /** Differences under this many dollars aren't chased. */
  toleranceDollars: number;
}

export const FLAG_DEFAULTS: Readonly<FlagSettings> = { notSentDays: 3, unpaidDays: 60, toleranceDollars: 5 };

export const FLAG_LIMITS: Readonly<Record<keyof FlagSettings, { min: number; max: number }>> = {
  notSentDays: { min: 1, max: 30 },
  unpaidDays: { min: 7, max: 365 },
  toleranceDollars: { min: 1, max: 100 },
};

export const FLAG_FOOTER_ID = 'follow-up-flags';
/** Route of Settings › Follow-up, for links from other pages. */
export const FOLLOW_UP_SETTINGS_PATH = '/settings/follow-up';

const WHAT: Record<keyof FlagSettings, string> = {
  notSentDays: 'Made but not sent',
  unpaidDays: 'Not paid',
  toleranceDollars: 'Differences not chased',
};

let state: FlagSettings = { ...FLAG_DEFAULTS };
const listeners = new Set<() => void>();
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getFlagSettings(): FlagSettings {
  return state;
}
export function useFlagSettings(): FlagSettings {
  return useSyncExternalStore(subscribe, getFlagSettings, getFlagSettings);
}
/** Applies from today on; nothing already flagged is recounted. */
export function setFlagSetting(key: keyof FlagSettings, next: number): void {
  const { min, max } = FLAG_LIMITS[key];
  const v = Math.max(min, Math.min(max, Math.round(next)));
  if (v === state[key]) return;
  const from = state[key];
  state = { ...state, [key]: v };
  recordSettingsChange(FLAG_FOOTER_ID, `${WHAT[key]}: ${String(from)} to ${String(v)}`);
  for (const l of listeners) l();
}
